import type Inputmask from 'inputmask'
// import Mask from 'inputmask'
import { defu } from 'defu'

type InputElement = HTMLTextAreaElement | HTMLInputElement
type Options = Inputmask.Options
type DirectiveBindingArgs = keyof typeof mask

function getInputElement(el: HTMLElement): InputElement {
  const inputElement = el instanceof HTMLInputElement ? el : el.querySelector('input')

  if (!inputElement) {
    throw new Error('No input element found')
  }

  return inputElement
}

async function initMask(el: HTMLElement, opts: Options) {
  const mask = (await import('inputmask')).default

  const element = getInputElement(el)

  new mask({
    showMaskOnHover: false,
    showMaskOnFocus: false,
    autoUnmask: true,
    rightAlign: false,
    ...opts,
  })
    .mask(element)
}

function destroyMask(el: HTMLElement) {
  const element = getInputElement(el)

  if (element.inputmask) {
    element.inputmask.remove()
    delete element.inputmask
  }
}

function mergeOptions(binding: DirectiveBinding<Inputmask.Options, string, DirectiveBindingArgs>): Inputmask.Options {
  const { arg, value } = binding
  let merged: Inputmask.Options = {}

  if (arg && mask[arg]) {
    merged = defu(mask[arg], {})
  }

  if (value && typeof value === 'object') {
    merged = defu(value, merged)
  }

  return merged
}

export default defineNuxtPlugin(nuxtApp => {
  nuxtApp.vueApp.directive<
    HTMLElement,
    Inputmask.Options,
    string,
    DirectiveBindingArgs
  >('inputmask', {
    async beforeMount(el, binding) {
      const options = mergeOptions(binding)

      if (!Object.keys(options).length) return

      await initMask(el, options)
    },
    async updated(el, binding) {
      const element = getInputElement(el)
      const options = mergeOptions(binding)

      if (Object.keys(options).length) {
        if (element.inputmask) {
          element.inputmask.option(options, true)

          if (element.value !== element.inputmask.unmaskedvalue()) {
            element.inputmask.setValue(element.value)
          }
        }
        else {
          await initMask(el, options)
        }
      }
      else {
        destroyMask(el)
      }
    },
    unmounted(el) {
      destroyMask(el)
    },
  })
})
